import Image from 'next/image'
import React from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import { CopilotToolResultContent } from '../ChatV2/CopilotToolResultContent'
import { NodeExecutionDetailsContent } from '../Flowchart/NodeExecutionDetailsContent'
import { WorkflowDetailsContent } from '../Flowchart/WorkflowDetailsContent'
import { WorkflowInputsContent } from '../Flowchart/WorkflowInputsContent'
import { WorkflowExecutionResultsContent } from '../Workflow/Results/WorkflowExecutionResultsContent'
import { SpreadsheetPreview } from '../WorkflowToolResults/shared/SpreadsheetPreview'
import { WorkflowToolResult } from '../WorkflowToolResults/WorkflowToolResult'

export type PanelContent =
  | { type: 'workflow-tool-result'; nodeExecution: NodeExecution; workflowConfig: WorkflowConfig }
  | { type: 'copilot-tool-result'; toolName: string; content: unknown; inputs?: unknown }
  | { type: 'file'; filePath: string; fileName: string; content: string; language?: string }
  | { type: 'image'; fileName: string; imageUrl: string; downloadUrl?: string; mimeType?: string }
  | {
      type: 'document'
      fileName: string
      documentUrl: string
      downloadUrl?: string
      mimeType?: string
    }
  | {
      type: 'spreadsheet'
      fileName: string
      spreadsheetUrl: string
      downloadUrl?: string
      mimeType?: string
    }
  | { type: 'custom'; title: string; children: React.ReactNode }
  | {
      type: 'node-execution-details'
      nodeId: string
      executionId?: string
      nodeExecution?: NodeExecution
      nodeData: any
      workflowConfig?: WorkflowConfig
    }
  | {
      type: 'workflow-inputs'
      inputs: Record<string, any>
      executionId: string
    }
  | {
      type: 'workflow-details'
      workflowConfig: WorkflowConfig
      workflowVersion?: number
    }
  | {
      type: 'workflow-execution-results'
      executionId: string
    }

interface PanelContentRendererProps {
  content: PanelContent
  className?: string
  onOpenPanel?: (content: PanelContent, breadcrumbs: any[]) => void
}

export function PanelContentRenderer({
  content,
  className = '',
  onOpenPanel,
}: PanelContentRendererProps) {
  switch (content.type) {
    case 'workflow-tool-result':
      return (
        <div className={`h-full w-full p-4 ${className}`}>
          <WorkflowToolResult
            nodeExecution={content.nodeExecution}
            workflowConfig={content.workflowConfig}
            onOpenPanel={onOpenPanel}
          />
        </div>
      )

    case 'copilot-tool-result':
      return (
        <div className={`h-full w-full ${className}`}>
          <CopilotToolResultContent
            toolName={content.toolName}
            content={content.content}
            inputs={content.inputs}
          />
        </div>
      )

    case 'file':
      return (
        <div className={`h-full w-full p-4 ${className}`}>
          <div className="h-full overflow-hidden rounded-3xl border border-gray-200/50 bg-white text-black shadow-sm">
            <div className="border-b border-gray-200/50 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900">{content.fileName}</div>
                <div className="text-xs text-gray-500">{content.filePath}</div>
              </div>
            </div>
            <div className="h-full overflow-auto p-4">
              <pre className="whitespace-pre-wrap break-words font-mono text-sm text-gray-900">
                {content.content}
              </pre>
            </div>
          </div>
        </div>
      )

    case 'image':
      return (
        <div className={`h-full w-full p-4 ${className}`}>
          <div className="h-full overflow-hidden rounded-3xl border border-gray-200/50 bg-white text-black shadow-sm">
            <div className="border-b border-gray-200/50 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900">{content.fileName}</div>
                {content.mimeType && (
                  <div className="text-xs text-gray-500">{content.mimeType}</div>
                )}
              </div>
            </div>
            <div className="flex h-full items-center justify-center p-4">
              <div className="max-h-full max-w-full">
                <Image
                  src={content.imageUrl}
                  alt={content.fileName}
                  width={800}
                  height={600}
                  className="h-auto max-h-full w-auto max-w-full rounded-2xl object-contain"
                  unoptimized
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                    const parent = target.parentElement
                    if (parent) {
                      parent.innerHTML = `
                        <div class="flex items-center justify-center h-full text-gray-500">
                          <div class="text-center">
                            <div class="h-12 w-12 mx-auto mb-2 bg-gray-300 rounded flex items-center justify-center">🖼️</div>
                            <p class="text-sm">Image preview unavailable</p>
                          </div>
                        </div>
                      `
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )

    case 'document':
      return (
        <div className={`h-full w-full p-4 ${className}`}>
          <div className="h-full overflow-hidden rounded-3xl border border-gray-200/50 bg-white text-black shadow-sm">
            <div className="border-b border-gray-200/50 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900">{content.fileName}</div>
                {content.mimeType && (
                  <div className="text-xs text-gray-500">{content.mimeType}</div>
                )}
              </div>
            </div>
            <div className="h-full p-4">
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(content.documentUrl)}&embedded=true`}
                width="100%"
                height="100%"
                className="rounded-2xl"
                title={content.fileName}
              />
            </div>
          </div>
        </div>
      )

    case 'node-execution-details':
      return (
        <div className={`h-full w-full ${className}`}>
          <NodeExecutionDetailsContent
            executionId={content.executionId}
            nodeExecution={content.nodeExecution}
            nodeData={content.nodeData}
            workflowConfig={content.workflowConfig}
            onOpenPanel={onOpenPanel}
          />
        </div>
      )

    case 'spreadsheet':
      return (
        <div className={`h-full w-full p-4 ${className}`}>
          <div className="h-full overflow-hidden rounded-3xl border border-gray-200/50 bg-white text-black shadow-sm">
            <div className="border-b border-gray-200/50 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900">{content.fileName}</div>
                {content.mimeType && (
                  <div className="text-xs text-gray-500">{content.mimeType}</div>
                )}
              </div>
            </div>
            <div className="h-full overflow-auto p-4">
              <SpreadsheetPreview url={content.spreadsheetUrl} maxRows={100} />
            </div>
          </div>
        </div>
      )

    case 'custom':
      return <div className={`h-full w-full ${className}`}>{content.children}</div>

    case 'workflow-inputs':
      return (
        <div className={`h-full w-full ${className}`}>
          <WorkflowInputsContent inputs={content.inputs} executionId={content.executionId} />
        </div>
      )

    case 'workflow-details':
      return (
        <div className={`h-full w-full ${className}`}>
          <WorkflowDetailsContent
            workflowConfig={content.workflowConfig}
            workflowVersion={content.workflowVersion}
          />
        </div>
      )

    case 'workflow-execution-results':
      return (
        <div className={`h-full w-full ${className}`}>
          <WorkflowExecutionResultsContent
            executionId={content.executionId}
            onOpenPanel={onOpenPanel}
          />
        </div>
      )

    default:
      return (
        <div className={`flex h-full w-full items-center justify-center ${className}`}>
          <div className="text-sm text-gray-500">Unknown content type</div>
        </div>
      )
  }
}

/**
 * Helper function to extract a display title from PanelContent
 */
export function getPanelContentTitle(content: PanelContent): string {
  switch (content.type) {
    case 'workflow-tool-result':
      // Try to get tool ID from workflow config
      const node = content.workflowConfig.nodes.find((n) => n.id === content.nodeExecution.nodeId)
      return node?.toolId || content.nodeExecution.nodeId

    case 'copilot-tool-result':
      return content.toolName

    case 'file':
      return content.fileName

    case 'image':
      return content.fileName

    case 'document':
      return content.fileName

    case 'spreadsheet':
      return content.fileName

    case 'node-execution-details':
      return content.nodeData?.name || 'Node Execution Details'

    case 'workflow-inputs':
      return 'Workflow Inputs'

    case 'workflow-details':
      return content.workflowConfig.name || 'Workflow Details'

    case 'workflow-execution-results':
      return 'Workflow Results'

    case 'custom':
      return content.title

    default:
      return 'Unknown'
  }
}

/**
 * Helper function to determine if content supports specific actions
 */
export function getPanelContentActions(content: PanelContent): string[] {
  switch (content.type) {
    case 'workflow-tool-result':
      return ['download', 'share', 'export-markdown']

    case 'copilot-tool-result':
      return []

    case 'file':
      return ['copy', 'download', 'edit']

    case 'image':
      return ['download', 'share']

    case 'document':
      return ['download', 'share']

    case 'spreadsheet':
      return ['download', 'share']

    case 'node-execution-details':
      return ['export-markdown']

    case 'workflow-inputs':
      return []

    case 'workflow-details':
      return ['export-markdown']

    case 'workflow-execution-results':
      return ['fork', 'load-to-context']

    case 'custom':
      return []

    default:
      return []
  }
}
